
const utils       		    = require('../lib/utils')
const appConfig       		= require('./config').getConfig()
const etagResourceHandler   = require('./etagResourceHandler')
const heartbeatConfig       = Object.freeze(appConfig.heartbeatConfig || {})


/**
 * handles heartbeat connection
 * @constructor
 */
function HeartbeatController(storage){
    this.storage = storage
    this.connected = false
    this.eventSource = null

    this.isDesktop = ((((((storage || {}).session || {}).user || {}).environment || {}).device || {}).type || 'desktop') === 'desktop'

    this.connectionIDChanged = false
    this.connectionID = null
    this.serverWasBusySinceLastConnection = false
    this.lastServerActivityTS = null

    ;(async () => {
        try {
            await this.connect()
        } catch (error){
            console.log(error)
        }
    })()
}

/**
 * handle connection state
 *
 * @param connected  boolean - connected
 */
HeartbeatController.prototype.stateChanged = function(connected){
    this.connected = !!connected

    if((this.storage || {}).componentTree){
        this.storage.componentTree.callHandler(null, 'heartbeat.connectionStateChanged', [this.connected])
    }

    if(this.connecting){
        if(this.connected)  this.connecting.resolve();
        else                this.connecting.reject();
        delete this.connecting
    }
}

/**
 * reconnect heartbeat if necessary and tell everyone afterward if necessary
 *
 * @param [forceReconnectMessage=false]      boolean - trigger reconnect message after successful connect or immediately if the connection has already been established.
 * @returns {Promise<void>}
 */
HeartbeatController.prototype.reconnect = async function(forceReconnectMessage = false){
    try {
        if(!this.connected && !this.connecting){
            await this.connect(forceReconnectMessage)
        } else if(forceReconnectMessage){
            await this.processMessage({ type: 'reconnect' })
        }
    } catch (error) {
        console.error('reconnect not possible', error)
    }
}

/**
 * connect heartbeat
 *
 * @param [forceReconnectMessage=false]      boolean - trigger reconnect message after successful connect even without the server indicating any changes.
 * @returns {Promise<void>}
 */
HeartbeatController.prototype.connect = async function(forceReconnectMessage = false){
    if(!heartbeatConfig.turnedOn){
        return
    }
    if(appConfig.heartbeatConfig.authenticatedClientsOnly && !((((this.storage || {}).user || {}).data || {}).states || {}).authenticated){
        return
    }
    if(((((this.storage || {}).request || {}).route || {}).config || {}).heartbeatControllerGranted === false){
        return
    }
    if(this.connecting){
        return
    }

    this.connecting = utils.promise.newCustom(this)

    if(this.eventSource){
        this.eventSource.close()
    }

    this.eventSource = new EventSource(`/LTR/~/heartbeat/${this.connectionID || ''}`, { withCredentials: false })

    this.eventSource.onerror = async event => {
        // use the connectionID within the url in order to keep it.
        if(this.connectionIDChanged){
            this.connectionIDChanged = false
            this.disconnect()
            try {
                await this.connect()
            } catch (error){
                console.log(error)
            }
        } else {
            this.stateChanged(false)
        }
    }
    this.eventSource.onmessage = async event => {
        const message = JSON.parse(event.data || '{}')

        // server want's me to close this connection for now.
        if(message.type === 'close'){
            this.disconnect()
            return
        }

        // heartbeat connection established
        if(message.type === 'connected'){
            this.serverWasBusySinceLastConnection = (this.lastServerActivityTS && this.lastServerActivityTS < message.lastActivityTS || message.serverWasBusySinceConnectionStart)
            this.connectionIDChanged = this.connectionID !== message.connectionID
            this.connectionID = message.connectionID
            this.lastServerActivityTS = message.lastActivityTS

            this.stateChanged(true)

            if(this.serverWasBusySinceLastConnection || forceReconnectMessage){
                forceReconnectMessage = false
                await this.processMessage({ type: 'reconnect' })
            }
            return
        }

        this.processMessage(message)
    }

    return this.connecting
}

/**
 * disconnect from heartbeat
 */
HeartbeatController.prototype.disconnect = function(){
    if(this.connected)      this.stateChanged(false); // change state if necessary
    if(this.eventSource)    this.eventSource.close(); // close eventSource event if in between connections
}

/**
 * process heartbeat message
 *
 * @param message   object - heartbeat message
 *                  format = {
 *                      type: '',
 *                      subType: '',
 *                      data: {<someData sent by the server>},
 *                  } || {
 *                      type: 'heartbeat',
 *                      heartbeats: [{
 *                          type: '',
 *                          subType: '',
 *                          data: {<someData sent by the server>},
 *                      }, ...]
 *                  }
 * @returns {Promise<void>}
 */
HeartbeatController.prototype.processMessage = async function(message){
    if(!message) return;
    if(!this.connected) return;

    if(message.type === 'heartbeats'){
        for(const heartbeat of message.heartbeats || []){
            this.processMessage(heartbeat)
        }
        return
    }

    const componentTree = (this.storage || {}).componentTree
    let heartbeatPrewarmedResourceHashes = {}
    const keys = [message.type || 'UNKNOWN']
    if(message.subType) keys.push(message.subType);
    message.heartbeatKey = keys.join('.')
    message.data = message.data || {}
    message.data.primaryKeys = message.data.primaryKeys || {} // usually primaryKeys list is set via server. in case of special messages (e.g., reconnect) those keys are not set.

    if(message.ts){
        this.lastServerActivityTS = message.ts
    }

    if(message.type){

        // prewarm registered resources
        if(message.type === 'resourceChanged' && message.subType){
            heartbeatPrewarmedResourceHashes = await etagResourceHandler.prewarmHeartbeat(message.heartbeatKey)
        }

        // call heartbeat handlers
        if(componentTree){
            componentTree.callHandler(null, `heartbeat.${message.type}`, [message, heartbeatPrewarmedResourceHashes || {}])
        }

    }
}

/**
 * called every time the document visibility has changed.
 *
 * @param documentHidden        boolean - is the tab / window active (in foreground) or not.
 */
HeartbeatController.prototype.onDocumentVisibilityChanged = function(documentHidden){
    if(!heartbeatConfig.turnedOn) return;
    if(this.isDesktop) return; // prevent desktops from disconnecting and reconnecting on every alt-tab.

    // mobile devices should disconnect if the tab or browser is not in foreground or the device is in standby ...
    if(documentHidden){
        this.disconnect()
    }
    // ... and reconnect after brought to foreground again.
    else {
        this.reconnect()
    }
}

module.exports = HeartbeatController